import { doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase.js";
import { seedLGPDCourse } from "../services/db-seed.js";

/**
 * Theme Loader - Injeta variáveis CSS dinâmicas baseadas no Firestore.
 * Segue as diretrizes de UI/UX e Design System do projeto.
 */
class ThemeLoader {
  constructor() {
    this.root = document.documentElement;
    this.defaultTheme = {
      primary: '#1E2A5A',
      secondary: '#87CECA',
      background: '#F4F6FA',
      text: '#FFFFFF'
    };
  }

  /**
   * Sanitiza a cor hex para evitar injeções arbitrárias de código CSS.
   * @param {string} color 
   * @returns {boolean}
   */
  isValidHexColor(color) {
    return typeof color === 'string' && /^#([0-9A-F]{3}){1,2}$/i.test(color);
  }

  async loadTheme(branchId) {
    // Caso o banco de dados do Firebase não esteja inicializado (modo de demonstração)
    if (!db) {
      console.warn('⚠️ Firebase não inicializado. Aplicando tema padrão.');
      this.applyTheme(this.defaultTheme);
      return;
    }

    try {
      const themeRef = doc(db, 'theme', branchId);
      const docSnap = await getDoc(themeRef);

      if (docSnap.exists()) {
        const theme = docSnap.data();
        this.applyTheme(theme);
      } else {
        console.info('ℹ️ Tema customizado não encontrado no Firestore. Usando fallback padrão.');
        this.applyTheme(this.defaultTheme);
      }
    } catch (error) {
      console.warn('⚠️ Falha ao buscar tema do Firestore. Fallback para tema padrão:', error);
      this.applyTheme(this.defaultTheme);
    }
  }

  applyTheme(theme) {
    // Sanitização e validação das cores injetadas
    const primaryColor = this.isValidHexColor(theme.primary) ? theme.primary : this.defaultTheme.primary;
    const secondaryColor = this.isValidHexColor(theme.secondary) ? theme.secondary : this.defaultTheme.secondary;

    // Cores principais no root
    this.root.style.setProperty('--primary', primaryColor);
    this.root.style.setProperty('--secondary', secondaryColor);

    // Gradientes dinâmicos calculados com segurança
    this.root.style.setProperty('--blue-gradient', 
      `linear-gradient(180deg, ${primaryColor} 0%, ${primaryColor}E6 50%, ${primaryColor}A3 100%)`
    );
    
    // Teal sempre mantido como marca registrada decorativa
    this.root.style.setProperty('--teal', secondaryColor);

    console.log('✅ Tema configurado com sucesso.');
  }
}

// Inicialização segura após o carregamento do DOM
document.addEventListener('DOMContentLoaded', () => {
  const loader = new ThemeLoader();
  loader.loadTheme('branch_main');
  injectCustomAlertStyles();
  setupCustomAlerts();
  
  if (db) {
    seedLGPDCourse();
  }
});

function injectCustomAlertStyles() {
  const styleId = 'custom-alert-styles';
  if (document.getElementById(styleId)) return;

  const styleEl = document.createElement('style');
  styleEl.id = styleId;
  styleEl.textContent = `
    .custom-alert-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(11, 27, 94, 0.45);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 11000;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.25s ease;
      font-family: var(--font-main, 'Lato', sans-serif);
    }
    
    .custom-alert-overlay.open {
      opacity: 1;
      pointer-events: auto;
    }
    
    .custom-alert-card {
      background: var(--bg-card, #ffffff);
      border-radius: var(--radius-md, 12px);
      width: 90%;
      max-width: 440px;
      box-shadow: var(--shadow-2xl, 0 20px 25px -5px rgba(0,0,0,0.15));
      border: 1px solid rgba(11, 27, 94, 0.08);
      transform: scale(0.92);
      transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
    
    .custom-alert-overlay.open .custom-alert-card {
      transform: scale(1);
    }
    
    .custom-alert-header {
      padding: var(--space-md, 16px) var(--space-lg, 24px);
      background: #F8FAFC;
      border-bottom: 1px solid rgba(11, 27, 94, 0.06);
      display: flex;
      align-items: center;
      gap: 12px;
    }
    
    .custom-alert-icon {
      font-size: 1.35rem;
    }
    
    .custom-alert-title {
      font-family: var(--font-display, 'Montserrat', sans-serif);
      font-weight: 700;
      color: var(--primary, #0B1B5E);
      margin: 0;
      font-size: 1.1rem;
      letter-spacing: 0.5px;
    }
    
    .custom-alert-body {
      padding: var(--space-lg, 24px);
      color: var(--text-primary, #333333);
      font-size: 0.95rem;
      line-height: 1.6;
    }
    
    .custom-alert-footer {
      padding: var(--space-md, 16px) var(--space-lg, 24px);
      border-top: 1px solid rgba(11, 27, 94, 0.06);
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      background: #F8FAFC;
    }
    
    .custom-alert-btn {
      padding: 10px 22px;
      border-radius: var(--radius-sm, 8px);
      font-family: var(--font-main, 'Lato', sans-serif);
      font-size: 0.9rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      border: none;
      outline: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    
    .custom-alert-btn-primary {
      background: var(--primary, #0B1B5E);
      color: #ffffff;
      box-shadow: 0 4px 6px -1px rgba(11, 27, 94, 0.15);
    }
    
    .custom-alert-btn-primary:hover {
      background: var(--teal, #2AABB8);
      transform: translateY(-1.5px);
      box-shadow: 0 6px 12px -2px rgba(42, 171, 184, 0.25);
    }
    
    .custom-alert-btn-primary:active {
      transform: translateY(0);
    }
    
    .custom-alert-btn-secondary {
      background: transparent;
      color: var(--text-secondary, #64748B);
      border: 1.5px solid rgba(11, 27, 94, 0.12);
    }
    
    .custom-alert-btn-secondary:hover {
      background: rgba(11, 27, 94, 0.04);
      color: var(--primary, #0B1B5E);
      border-color: var(--primary, #0B1B5E);
    }
  `;
  document.head.appendChild(styleEl);
}

function setupCustomAlerts() {
  // Sobrescrever window.alert
  window.alert = function (message) {
    let overlay = document.getElementById('custom-alert-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'custom-alert-overlay';
      overlay.className = 'custom-alert-overlay';
      document.body.appendChild(overlay);
    }

    overlay.innerHTML = `
      <div class="custom-alert-card">
        <div class="custom-alert-header">
          <span class="custom-alert-icon">🔔</span>
          <h3 class="custom-alert-title">Notificação</h3>
        </div>
        <div class="custom-alert-body">${message}</div>
        <div class="custom-alert-footer">
          <button class="custom-alert-btn custom-alert-btn-primary" id="custom-alert-ok-btn">OK</button>
        </div>
      </div>
    `;

    setTimeout(() => overlay.classList.add('open'), 10);

    return new Promise((resolve) => {
      const okBtn = document.getElementById('custom-alert-ok-btn');
      const closeAlert = () => {
        overlay.classList.remove('open');
        setTimeout(() => {
          overlay.innerHTML = '';
          resolve();
        }, 250);
      };
      
      okBtn.addEventListener('click', closeAlert);

      const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Escape') {
          e.preventDefault();
          document.removeEventListener('keydown', handleKeyDown);
          closeAlert();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
    });
  };

  // Definir utilitário global window.showConfirm
  window.showConfirm = function (message) {
    return new Promise((resolve) => {
      let overlay = document.getElementById('custom-alert-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'custom-alert-overlay';
        overlay.className = 'custom-alert-overlay';
        document.body.appendChild(overlay);
      }

      overlay.innerHTML = `
        <div class="custom-alert-card">
          <div class="custom-alert-header">
            <span class="custom-alert-icon">❓</span>
            <h3 class="custom-alert-title">Confirmação</h3>
          </div>
          <div class="custom-alert-body">${message}</div>
          <div class="custom-alert-footer">
            <button class="custom-alert-btn custom-alert-btn-secondary" id="custom-alert-cancel-btn">Cancelar</button>
            <button class="custom-alert-btn custom-alert-btn-primary" id="custom-alert-confirm-btn">Confirmar</button>
          </div>
        </div>
      `;

      setTimeout(() => overlay.classList.add('open'), 10);

      const confirmBtn = document.getElementById('custom-alert-confirm-btn');
      const cancelBtn = document.getElementById('custom-alert-cancel-btn');

      const cleanUp = (result) => {
        overlay.classList.remove('open');
        setTimeout(() => {
          overlay.innerHTML = '';
          resolve(result);
        }, 250);
      };

      confirmBtn.addEventListener('click', () => cleanUp(true));
      cancelBtn.addEventListener('click', () => cleanUp(false));

      const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          document.removeEventListener('keydown', handleKeyDown);
          cleanUp(true);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          document.removeEventListener('keydown', handleKeyDown);
          cleanUp(false);
        }
      };
      document.addEventListener('keydown', handleKeyDown);
    });
  };
}

// Global showToast fallback — disponível em todas as páginas
if (typeof window.showToast !== 'function') {
  const toastStyle = document.createElement('style');
  toastStyle.textContent = `
    #global-toast {
      position: fixed; bottom: 80px; left: 50%; transform: translateX(-50%);
      padding: 16px 24px; border-radius: 12px; font-weight: 600; font-size: 0.9rem;
      z-index: 10001; box-shadow: 0 8px 24px rgba(0,0,0,0.2);
      max-width: 90%; text-align: center; transition: all 0.3s ease;
      animation: slideUpToast 0.3s ease;
    }
    @keyframes slideUpToast {
      from { bottom: -60px; opacity: 0; }
      to { bottom: 80px; opacity: 1; }
    }
  `;
  document.head.appendChild(toastStyle);

  window.showToast = function (message, type) {
    const existing = document.getElementById('global-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = 'global-toast';
    toast.style.background =
      type === 'success' ? 'var(--success, #22c55e)' :
      type === 'error' ? 'var(--danger, #ef4444)' :
      'var(--blue-deep, #0B1B5E)';
    toast.style.color = '#fff';
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'slideUpToast 0.3s ease reverse';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  };
}

export { ThemeLoader };

